import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AuthContext, IntlContext } from "context";
import { Plus } from "lucide-react";
import { adKeys, fetchAds } from "hooks/ad";
import { AdInterface } from "types/ad";
import { Role, Status } from "types/user";
import { ROUTES } from "routes";
import { Button } from "components/ui/button";
import { Loader } from "ui/Loader";
import AdCard from "./AdCard";

const MesAnnoncesList: React.FC = () => {
    const intl = useContext(IntlContext);
    const { account, currentRole } = useContext(AuthContext);

    const {
        data: ads = [],
        isLoading,
        refetch,
    } = useQuery({
        queryKey: adKeys.lists(),
        queryFn: () => fetchAds(),
        enabled: currentRole === Role.CLIENT,
    });

    const adList = ads as AdInterface[];

    const activeCount = useMemo(
        () => adList.filter((ad) => ad.is_active).length,
        [adList]
    );

    if (currentRole !== Role.CLIENT) {
        return null;
    }

    if (account?.user?.status !== Status.ACTIVE) {
        return (
            <div className="rounded-2xl bg-white p-6 shadow-sm sm:p-8">
                <h2 className="text-2xl font-bold text-gray-900">Mes annonces</h2>
                <p className="mt-4 text-sm text-slate-600">
                    {intl.formatMessage("service.label.to_validate")}
                </p>
            </div>
        );
    }

    if (isLoading && adList.length === 0) {
        return <Loader />;
    }

    return (
        <div className="rounded-2xl bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Mes annonces</h2>
                    <p className="mt-1 text-sm text-slate-500">
                        {activeCount} annonce{activeCount > 1 ? "s" : ""} active{activeCount > 1 ? "s" : ""}
                    </p>
                </div>
                <Button asChild className="rounded-xl">
                    <Link to={ROUTES.MY_ADS_CREATE}>
                        <Plus className="h-4 w-4" />
                        {intl.formatMessage("ad.label.add")}
                    </Link>
                </Button>
            </div>

            {adList.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-slate-50 px-6 py-12 text-center">
                    <p className="text-sm text-slate-600">Aucune annonce publiée pour le moment.</p>
                    <Button asChild className="mt-4 rounded-xl">
                        <Link to={ROUTES.MY_ADS_CREATE}>{intl.formatMessage("ad.label.add")}</Link>
                    </Button>
                </div>
            ) : (
                <div className="space-y-4">
                    {adList.map((ad) => (
                        <AdCard key={ad.id} ad={ad} onUpdated={() => void refetch()} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default MesAnnoncesList;
